import { useDateTimeUtils } from "@/use/datetime-utils";
import { type RangeMode, generateTimeRangeOffset } from "@/utils/time-range";
import { Serializer } from "@vueuse/core";
import { DateTime, Duration } from "luxon";
import { computed, Ref, shallowRef, watch } from "vue";
import { createRestrictiveInjectionState } from "../../composable/restrictive-injection-state";
import { useStaticQueryParam } from "../static-query";
import { TimeRange } from "./types";

export type TimeRangeManagerOptions = {
	defaultRange?: RangeMode;
};

export const [provideTimeRangeManager, useTimeRangeManager] = createRestrictiveInjectionState(
	(
		timezone: MaybeRefOrGetter<string | null | undefined>,
		locale: MaybeRefOrGetter<string | null | undefined>,
		opts: TimeRangeManagerOptions = {}
	) => {
		const dateTimeProperties = computed(() => ({
			timezone: toValue(timezone),
			locale: toValue(locale),
		}));
		const { updateDateTimeProperties, dateTimeEquals } = useDateTimeUtils();

		function updateDateTimeRefsProperties(timeRefs: Ref<DateTime | TimeRange>[]) {
			for (const timeRef of timeRefs) {
				if (timeRef.value instanceof DateTime) {
					timeRef.value = updateDateTimeProperties(timeRef.value, dateTimeProperties.value);
				} else {
					timeRef.value = {
						start: updateDateTimeProperties(timeRef.value.start, dateTimeProperties.value),
						end: updateDateTimeProperties(timeRef.value.end, dateTimeProperties.value),
					};
				}
			}
		}

		const rangeMode = useStaticQueryParam<RangeMode>("mode", {
			defaultVal: opts.defaultRange ?? "last_8h",
		});

		const dateTimeSerializer: Serializer<DateTime> = {
			read: (v) => updateDateTimeProperties(DateTime.fromISO(v), dateTimeProperties.value),
			write: (v) => v.toISO() || "",
		};

		let defaultStartTime: DateTime;
		if (rangeMode.value === "custom") {
			defaultStartTime = DateTime.now().minus({ hour: 8 });
		} else {
			defaultStartTime = DateTime.now().minus(generateTimeRangeOffset(rangeMode.value));
		}

		const startTime = useStaticQueryParam<DateTime>("start", {
			condition: () => rangeMode.value === "custom",
			defaultVal: defaultStartTime,
			serializer: dateTimeSerializer,
		});
		const endTime = useStaticQueryParam<DateTime>("end", {
			condition: () => rangeMode.value === "custom",
			defaultVal: DateTime.now(),
			serializer: dateTimeSerializer,
		});

		const range = shallowRef({
			start: startTime.value,
			end: endTime.value,
		});

		watch(range, () => {
			if (range.value.start.valueOf() !== startTime.value.valueOf()) {
				startTime.value = range.value.start;
			}

			if (range.value.end.valueOf() !== endTime.value.valueOf()) {
				endTime.value = range.value.end;
			}
		});

		const cursor = useStaticQueryParam<DateTime>("cursor", {
			serializer: dateTimeSerializer,
			defaultVal: DateTime.now(),
		});

		const zoomRange = shallowRef({
			start: range.value.start,
			end: range.value.end,
		});

		const zoomRangeOffset = shallowRef({
			...zoomRange.value,
		});

		watch(
			[timezone, locale],
			() => {
				if (!toValue(timezone) || !toValue(locale)) return;
				updateDateTimeRefsProperties([range, cursor, zoomRange, zoomRangeOffset]);
			},
			{ immediate: true }
		);

		const zoomWindow = computed(() => {
			if (!zoomRange.value) return Duration.fromMillis(0);
			const { start, end } = zoomRange.value;

			return end.diff(start);
		});

		watch(range, () => {
			if (range.value.end < cursor.value) {
				updateCursor(range.value.end.toJSDate());
			} else if (cursor.value < range.value.start) {
				updateCursor(range.value.start.toJSDate());
			}
		});

		function createDateTime(date: Date | DateTime) {
			if (DateTime.isDateTime(date)) return date;

			const properties = { timezone: toValue(timezone), locale: toValue(locale) };
			return updateDateTimeProperties(DateTime.fromJSDate(date), properties);
		}

		function timeRangeEquals(
			range: TimeRange<DateTime | Date>,
			newRange: TimeRange<DateTime | Date>
		) {
			return dateTimeEquals(range.start, newRange.start) && dateTimeEquals(range.end, newRange.end);
		}

		function updateRange(newRange: TimeRange<Date | DateTime>, updateZoom = true) {
			if (timeRangeEquals(range.value, newRange)) return;

			if (rangeMode.value === "custom") {
				updateCustomRange(newRange);
			} else {
				updatePredefinedRange(newRange.end);
			}

			if (updateZoom) {
				zoomRange.value = { ...range.value };
			}
		}

		function updateRangeMode(mode: RangeMode) {
			rangeMode.value = mode;

			if (rangeMode.value !== "custom") {
				// Since we are not in cursom mode, update range will compute the start time
				updateRange({ start: new Date(), end: new Date() });
			}
		}

		function updatePredefinedRange(time: Date | DateTime) {
			if (rangeMode.value === "custom") return;

			range.value = {
				start: createDateTime(
					new Date(time.valueOf() - generateTimeRangeOffset(rangeMode.value).as("millisecond"))
				),
				end: createDateTime(time),
			};
		}

		function updateCustomRange(newRange: TimeRange<Date | DateTime>) {
			rangeMode.value = "custom";
			range.value = {
				start: createDateTime(newRange.start),
				end: createDateTime(newRange.end),
			};
		}

		function updateZoom(newZoom: TimeRange<Date | DateTime>) {
			if (timeRangeEquals(zoomRange.value, newZoom)) return;

			zoomRange.value = {
				start: createDateTime(newZoom.start),
				end: createDateTime(newZoom.end),
			};
		}

		function updateCursor(date: Date | DateTime) {
			if (dateTimeEquals(cursor.value, date)) return;
			cursor.value = createDateTime(date);
		}

		return {
			rangeMode,
			range,
			cursor,
			zoomRange,
			zoomRangeOffset,
			zoomWindow,
			createDateTime,
			updateRange,
			updateRangeMode,
			updateZoom,
			updateCursor,
			updateCustomRange,
		};
	}
);
